import React, { useState, useRef, useEffect } from 'react';

interface ButtonProps {
  onChange: (value: string) => void;
  options: string[];
  textInput: string;
  value?: string;
  className?: string;
}

const CustomDropdown: React.FC<ButtonProps> = ({
  onChange,
  options,
  value,
  textInput,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHoveredIndex(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => setIsOpen((open) => !open);

  const selectOption = (option: string) => {
    onChange(option);
    setIsOpen(false);
    setHoveredIndex(null);
  };

  const selectedLabel = value
    ? options
        .find((o) => o === value)
        ?.charAt(0)
        .toUpperCase() + value.slice(1)
    : textInput;

  return (
    <div ref={dropdownRef} className={`custom-dropdown ${className}`}>
      <div
        onClick={toggleDropdown}
        className="select-box"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleDropdown();
          }
        }}
      >
        <span>{selectedLabel}</span>
        <span className={`arrow ${isOpen ? 'open' : ''}`} aria-hidden="true"></span>
      </div>

      <div role="listbox" tabIndex={-1} className={`options-list ${isOpen ? 'open' : ''}`}>
        {options.map((option, index) => {
          const isSelected = value === option;
          const isHovered = hoveredIndex === index;
          return (
            <div
              key={option}
              role="option"
              aria-selected={isSelected}
              tabIndex={0}
              className={`option ${isSelected ? 'selected' : ''} ${isHovered ? 'hover' : ''}`}
              onClick={() => selectOption(option)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  selectOption(option);
                }
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomDropdown;
